import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useTranslation } from 'react-i18next';

import type { IModuleField, IRecordValueInput } from '@/features/module-builder/types';
import { evaluateDependencies } from '@/lib/record-form/dependencies';
import {
    buildRecordSchema,
    isWritableField,
    type RecordFormValues,
} from '@/lib/record-form/schema';
import { buildFormDefaults, toRecordValueInputs } from '@/lib/record-form/values';

export interface UseRecordFormOptions {
    fields: IModuleField[];
    /** Snapshot to edit (values keyed by field NAME); omit/null for create. */
    source?: Record<string, unknown> | null;
}

export interface UseRecordFormResult {
    form: UseFormReturn<RecordFormValues>;
    /** Field NAMES hidden by dependency rules for the current values. */
    hidden: Set<string>;
    /** Field NAMES currently (effectively) required. */
    required: Set<string>;
    /** Current form values → `{ fieldId, value }` API payload. */
    serialize: () => IRecordValueInput[];
}

/**
 * Form state for a dynamic record form: zod schema resolver, defaults seeded
 * from the source snapshot (or field defaults on create), live dependency
 * evaluation, and API payload serialization.
 */
export function useRecordForm({ fields, source }: UseRecordFormOptions): UseRecordFormResult {
    const { t } = useTranslation();

    const schema = useMemo(() => buildRecordSchema(fields, t), [fields, t]);
    const writableFields = useMemo(() => fields.filter(isWritableField), [fields]);

    const form = useForm<RecordFormValues>({
        resolver: standardSchemaResolver(schema) as Resolver<RecordFormValues>,
        defaultValues: buildFormDefaults(fields, source),
        mode: 'onSubmit',
    });

    // Reset to fresh defaults when the source record changes identity
    // (e.g. the edit dialog is reopened for a different record).
    const previousSourceRef = useRef(source);
    useEffect(() => {
        if (previousSourceRef.current === source) return;
        previousSourceRef.current = source;
        form.reset(buildFormDefaults(fields, source));
    }, [fields, form, source]);

    // Record forms are small — re-evaluating dependencies each render is cheap
    // and keeps hidden/required perfectly in sync with the watched values.
    // (`useWatch` instead of `form.watch()` for React Compiler compatibility.)
    const values = useWatch({ control: form.control });
    const { hidden, required } = evaluateDependencies(writableFields, values);

    const serialize = useCallback(
        () => toRecordValueInputs(fields, form.getValues()),
        [fields, form],
    );

    return { form, hidden, required, serialize };
}
